<template>
  <card-wrapper
    :max-width="history ? null : 'min(90vw, 400px)'"
    :isHeightDisabled="true"
    class="reward-options-detail-card"
    :on-click="openConfig" 
    :is-active="isInCart"
    :clear-active="clear"
    override-text-body-padding-left="22px" >
  >
    <template v-slot:textBody>
      <div @click.stop="">
        <v-row class="header-row align-center no-gutters">
          <v-col cols="12" class="d-flex align-center justify-space-between"> <div class="detail-header reward-title">
              <strong>{{ rewardOption.rewardName || rewardOption.name }}</strong>
            </div>
          </v-col>
        </v-row>
        
        <!-- Rules section with discount and valid items -->
        <div v-if="hasDiscountOrValidItems || (history && (getDiscountText || hasValidItems))" class="rules-section">
          <ul class="rules-list">
            <li v-if="getDiscountText" class="rule-item">
              {{ getDiscountText }}
            </li>
            <li v-if="hasValidItems" class="rule-item">
              Valid for select items
            </li>
          </ul>
        </div>
        
        <v-row class="no-gutters">
          <v-col cols="12">
            <ul class="reward-description-list">
              <template v-if="!showAllDetails">
                <li class="description-collapsed">
                  {{ descriptionRows[0] }}
                </li>
                <li v-if="descriptionRows.length > 1" class="description-collapsed-ellipsis">
                  {{ descriptionRows[1] }}
                  <span v-if="descriptionRows.length > 2">…</span>
                </li>
              </template>
              <template v-else>
                <li v-for="(row, idx) in descriptionRows" :key="idx" class="description-expanded">
                  {{ row }}
                </li>
              </template>
            </ul>
            <div v-if="descriptionRows.length > 2" class="more-detail-link" @click.stop="showAllDetails = !showAllDetails">
              More Details
            </div>
          </v-col>
        </v-row>
        <v-row style="display:flex; justify-content: space-between;" class="mt-3 point-redemption-summary-row align-center no-gutters">
          <v-col cols="6" class="text-left">
            <span class="point-redemption-label-normal">Point Redemption</span>
          </v-col>
          <v-col cols="6" class="text-right">
            <span class="point-redemption-value-normal">
              {{ rewardOption.redeemPoints > 0 ? '-' : '' }}{{ rewardOption.redeemPoints | comma }} Points
            </span>
          </v-col>
        </v-row>
      </div>
    </template>

    <template v-slot:description>
      <div v-if="redeemedItems.length > 0" class="redeemed-items-section mt-1">
        <div v-for="(item, itemIdx) in redeemedItems" :key="itemIdx" class="redeemed-item-entry">
          <v-row style="display:flex; justify-content: space-between" class="align-center no-gutters">
            <v-col cols="auto" class="flex-grow-0 pr-0">
              <span class="item-name"> <strong>{{ item.qty }} - {{ item.name }}</strong></span>
            </v-col>
            <v-col cols="auto" class="text-right">
              <span class="item-price">{{ item.price | currency }}</span>
            </v-col>
          </v-row>

          <v-row v-for="(attr, attrIdx) in item.attributes" :key="`${itemIdx}-attr-${attrIdx}`" class="no-gutters item-attribute-row">
            <v-col cols="12" class="pl-4 d-flex justify-space-between align-center"> <div> <span class="item-attribute-qty" v-if="attr.qty && attr.qty !== 1">{{ attr.qty }}</span>
                <span class="item-attribute-name">{{ attr.name }}</span>
              </div>
              <span class="item-attribute-price-value">{{ (attr.price || attr.itemPrice || 0) | currency }}</span> </v-col>
          </v-row>
          </div>
      </div>

      <v-row v-if="isDisabled" class="no-gutters mt-2">
        <v-col cols="12" class="text-center red--text caption">
          {{ $vuetify.t('$vuetify.account.rewards.inPersonRedeemable') }}
        </v-col>
      </v-row>
      <v-row v-if="formattedExpiryDate && !history">
        <v-col cols="12" class="offer-expiry-row">
          <span class="offer-expiry">{{ hasActualExpiration ? $vuetify.t('$vuetify.account.rewards.offerExpires', [formattedExpiryDate]) : $vuetify.t('$vuetify.account.rewards.noExpiration') }}</span>
        </v-col>
      </v-row>
    </template>
  </card-wrapper>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import get from 'lodash/get';
import moment from 'moment';

import AttributeQuantitySelector from "../AttributeQuantitySelector";
import CardWrapper from "@/components/Wrappers/CardWrapper";
import { getCurrencySymbol } from '@/services/currency.service';
import { matchTranslation } from '../../services/util.service';

export default {
  components: {
    AttributeQuantitySelector,
    CardWrapper
  },

  props: {
    rewardOption: {
      type: Object,
      required: true
    },
    isFromHome: {
      type: Boolean,
      default: false
    },
    history: {
      type: Boolean,
      default: false
    },
    immutable: {
      type: Boolean,
      default: false
    },
    information: {
      type: Boolean,
      default: false
    },
    allowConfig: {
      type: Boolean,
      default: true
    },
    clear: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      showAllDetails: false,
    }
  },
  computed: {
    isInCart() {
      return !!(this.allowConfig && this.getActiveRewards && this.getActiveRewards.id == this.rewardOption.id)
    },
    
    getDiscountText() {
      const discountRule = this.rewardOption.discountRule;
      const symbol = getCurrencySymbol(this.getCurrencyCode);
      if (discountRule) {
        if (discountRule.percent) {
          return `${discountRule.percent * 100}% off`;
        } else if (discountRule.amount) {
          return `${symbol}${discountRule.amount} off`;
        }
      }

      if (this.rewardOption.discountAmount && this.rewardOption.discountAmount > 0) {
        return `${symbol}${this.rewardOption.discountAmount} off`;
      }

      const orderItem = get(this.rewardOption, 'orderItem');
      if (orderItem && orderItem.discountTotal && orderItem.discountTotal > 0) {
        return `${symbol}${orderItem.discountTotal} off`;
      }

      if (this.history && orderItem && orderItem.childItemList && orderItem.childItemList.length > 0) {
        const hasZeroPriceItems = orderItem.childItemList.some(child => 
          child.childItemList && child.childItemList.some(item => item.price === 0)
        );
        if (hasZeroPriceItems && this.rewardOption.redeemPoints > 0) {
          return "Discount applied";
        }
      }
      
      return null;
    },
    
    hasValidItems() {
      const itemRelationList = get(this.rewardOption, 'menuItem.itemRelationList', []);
      const hasTargets = itemRelationList.some(relation => 
        get(relation, 'targets', []).length > 0
      );
      
      if (hasTargets) return true;
      
      const orderItem = get(this.rewardOption, 'orderItem');
      if (orderItem && orderItem.childItemList && orderItem.childItemList.length > 0) {
        const hasSpecificItems = orderItem.childItemList.some(child => {
          return child.childItemList && child.childItemList.length > 0 && child.relation;
        });
        return hasSpecificItems;
      }
      
      return false;
    },
    
    hasDiscountOrValidItems() {
      return this.getDiscountText || this.hasValidItems;
    },
    
    descriptionRows() {
      const rows = [];

      const itemRelationList = get(this.rewardOption, 'menuItem.itemRelationList', []);
      const hasTargets = itemRelationList.some(relation => 
        get(relation, 'targets', []).length > 0
      );

    //   if (hasTargets) {
    //     rows.push("Valid for select items");
    //   }

      // const mainRewardTranslations = get(this.rewardOption, 'rewardOption.translations', []);
      // const translatedDesc = matchTranslation(mainRewardTranslations);

      // if (translatedDesc && translatedDesc.trim() !== '' && !rows.includes(translatedDesc)) {
      //     rows.push(translatedDesc);
      // }
      return rows;
    },
    formattedExpiryDate() {
      if (this.rewardOption.expireDate) {
        return moment(this.rewardOption.expireDate).format('YYYY/MM/DD');
      }
      if (this.rewardOption.validTo) {
        return moment(this.rewardOption.validTo).format('YYYY-MM-DD');
      }
      return null;
    },
    hasActualExpiration() {
      return !!(this.rewardOption.expireDate || this.rewardOption.validTo);
    },
    redeemedItems() {
      const items = [];
      const orderItem = get(this.rewardOption, 'orderItem');

      if (orderItem && orderItem.childItemList && orderItem.childItemList.length > 0) {
        orderItem.childItemList.forEach(childItem => {
          if (childItem.childItemList && childItem.childItemList.length > 0) {
            childItem.childItemList.forEach(grandChildItem => {
              const item = {
                qty: grandChildItem.qty,
                name: matchTranslation(get(grandChildItem, 'nameTranslation.translations', [])),
                price: grandChildItem.price || grandChildItem.itemPrice || 0,
                attributes: []
              };

              if (grandChildItem.itemAttributeList && grandChildItem.itemAttributeList.length > 0) {
                grandChildItem.itemAttributeList.forEach(attr => {
                  if (attr.attrValueList && attr.attrValueList.length > 0) {
                    attr.attrValueList.forEach(attrValue => {
                      item.attributes.push({
                        qty: attrValue.qty,
                        name: matchTranslation(get(attrValue, 'nameTranslation.translations', [])),
                        price: attrValue.price
                      });
                    });
                  }
                });
              }
              items.push(item);
            });
          }
        });
      }
      return items;
    },
    currentStoreId() {
      return get(this.getStore, 'storeId', null);
    },
    currentQuantity() {
      return this.rewardOption.qty || 0;
    },
    maxRedeemableQuantity() {
      if (!this.rewardOption.redeemPoints || this.rewardOption.redeemPoints <= 0) {
        return 0;
      }
      const totalCustomerPoints = get(this.getCustomerRewards, 'points', 0);
      const pointsUsedByOtherRewards = this.getUsedPointsInExistingOrder(this.rewardOption.id);
      const effectiveAvailablePoints = totalCustomerPoints - pointsUsedByOtherRewards;

      return Math.floor(effectiveAvailablePoints / this.rewardOption.redeemPoints);
    },
    unitText() {
        return 'rewards';
    },
    isDisabled() {
      return this.rewardOption && !this.rewardOption.onlineRedeemable && !this.history;
    },

    ...mapGetters([
      'getStore',
      'getCustomerRewards',
      'getExistingOrder',
      'getActiveRewards',
      'getCurrencyCode'
    ])
  },
  methods: {
    checkRewardValididity(reward) {
      if (!this.allowConfig || !reward)
        return false

      if (this.isDisabled) {
        this.$store.dispatch('showSnack', {
          text: this.$vuetify.t('$vuetify.account.rewards.inPersonRedeemable'),
          color: 'error'
        })

        return false
      }

      if (!this.canAfford(reward)) {
        this.$store.dispatch('showSnack', {
          text: this.$vuetify.t('$vuetify.account.rewards.insufficientFunds'),
          color: 'error'
        })

        return false
      }

      if (this.getActiveRewards && reward && this.getActiveRewards.id != reward.id) {
        this.$store.dispatch('showSnack', {
          text: this.$vuetify.t('$vuetify.account.rewards.removeFirst'),
          color: 'error'
        })

        return false
      }

      if (!('menuItem' in reward) && this.getActiveRewards && this.getActiveRewards.id == reward.id) {
        this.showSnack({
          text: this.$vuetify.t('$vuetify.account.rewards.alreadyInCart', [reward.rewardName]),
          color: 'secondary'
        })

        this.showCart()
      }

      return true
    },
    showCart() {
      if (this.$vuetify.breakpoint.smAndDown)
        this.$bus.$emit('toggleCart', true)
    },
    openConfig(reward = this.rewardOption) {
      if (!this.checkRewardValididity(reward))
        return

      if ('menuItem' in reward) {
        if (this.getActiveRewards && this.getActiveRewards.id == reward.id) {
          this.setIsCartItem(true)
          this.SET_REWARDS_CONFIG(this.getActiveRewards || reward)
        } else {
          this.SET_REWARDS_CONFIG(reward)
        }
      } else if (!this.getActiveRewards) {
        this.SET_ACTIVE_REWARDS(reward)

        this.showSnack({
          text: this.$vuetify.t('$vuetify.common.addedToCart', [reward.rewardName]),
          color: 'secondary'
        })

        this.fetchShoppingCart()
        this.showCart()
      }
    },
    clear() {
      this.SET_ACTIVE_REWARDS(null)
      this.fetchShoppingCart()
    },
    translatedDescription(translations) {
      if (Array.isArray(translations)) {
        return matchTranslation(translations);
      }
      return '';
    },
    canAfford(rewardOption) {
      const totalCustomerPoints = get(this.getCustomerRewards, 'points', 0);
      const pointsNeededForCurrentReward = rewardOption.redeemPoints;
      const pointsUsedByOtherRewards = this.getUsedPointsInExistingOrder(rewardOption.id);

      return (totalCustomerPoints - pointsUsedByOtherRewards) >= pointsNeededForCurrentReward;
    },
    getUsedPointsInExistingOrder(currentRewardId) {
        let previousRewardsSum = 0;
        if (this.getExistingOrder && this.getExistingOrder.length) {
            previousRewardsSum = this.getExistingOrder.reduce((accumulator, order) => {
                if (order.redemptionItems && order.redemptionItems.length) {
                    return (accumulator || 0) + order.redemptionItems.reduce((subSum, redeemedItem) => {
                        if (redeemedItem.rewardId === currentRewardId) {
                             return subSum + (redeemedItem.qty * redeemedItem.redeemPoints);
                        } else {
                            return subSum + (redeemedItem.qty * redeemedItem.redeemPoints);
                        }
                    }, 0);
                }
                return accumulator;
            }, 0);
        }
        return previousRewardsSum;
    },
    onChange(amountSelected, rewardOption) {
      if (!this.canAfford(rewardOption) && amountSelected > this.currentQuantity) {
        this.showSnack({
          text: this.$vuetify.t('$vuetify.account.rewards.insufficientFunds'),
          color: 'error'
        });
        return;
      }
      if (this.isDisabled) {
        this.showSnack({
          text: this.$vuetify.t('$vuetify.account.rewards.inPersonRedeemable'),
          color: 'error'
        });
        return;
      }

      this.$set(rewardOption, 'storeId', this.currentStoreId);
      this.$set(rewardOption, 'qty', amountSelected);

      this.$emit('update-redemption', { rewardOption, amount: amountSelected });

      this.fetchShoppingCart();
    },
    getImage(rewardOption) {
      if (rewardOption.imageURL)
        return `${process.env.VUE_APP_API_IMAGE}${rewardOption.imageURL}`

      if ('REWARD' in this.getStore.images && this.getStore.images.REWARD[0] && this.getStore.images.REWARD[0].url)
        return `${process.env.VUE_APP_API_IMAGE}${this.getStore.images.REWARD[0].url}`

      return null
    },
    onImageError($error, rewardOption) {
      console.error('Image Error', $error);
      rewardOption.imageURL = null
    },
    ...mapMutations([ 'SET_REWARDS_CONFIG', 'SET_ACTIVE_REWARDS' ]),
    ...mapActions(['fetchShoppingCart', 'fetchAccountDetails', 'showSnack', 'setIsCartItem']),
  },
  filters: {
    localCurrency(value) {
      // Note: This local filter doesn't have access to store, use global | currency filter instead
      const numValue = parseFloat(value);
      return isNaN(numValue) ? '0.00' : numValue.toFixed(2);
    },
    comma(value) {
      if (value == null) return '';
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  }
};
</script>

<style scoped>
.reward-options-detail-card {
  border: 1px solid var(--v-primary-base) !important;
  border-radius: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  background-color: white;
  width: 100%;
}

.detail-header {
  font-size: 18px;
  font-weight: 600;
  color: var(--v-primary-base);
  width: 100%;
}

.reward-title {
  font-size: 16px !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.rules-section {
  margin-top: 8px;
  margin-bottom: 8px;
}

.rules-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.rule-item {
  font-size: 14px;
  color: grey;
  position: relative;
  padding-left: 1.2em;
  margin-bottom: 4px;
}

.rule-item::before {
  content: "•";
  position: absolute;
  left: 0;
  color: inherit;
}


.close-btn {
  justify-content: end !important;
  color: #9E9E9E !important;
}

.field-value-row {
  align-items: center;
  display: flex !important;
  width: 100% !important;
  margin-bottom: 0px !important;
  justify-content: space-between !important;
  margin-left: 5px;
  padding-right: 10px;
}

.field-label {
  font-size: 12px;
  color: #767676 !important;
  font-weight: 500;
  display: inline-block;
  text-align: left;
}

.field-value {
  font-size: 12px;
  color: #767676 !important;
  font-weight: 600;
  display: inline-block;
  text-align: right;
}

.reward-description-list {
  list-style-type: disc; 
  color: #757575;
  font-size: 14px;
  margin-top: 0;
  margin-bottom: 8px;
  padding-left: 0 !important; 
}

.reward-description-list li {
  margin-bottom: 4px;
  word-break: break-word; 
  overflow: visible; 
  list-style: none;
  position: relative; 
  padding-left: 1.425em; 
}

.reward-description-list li::before {
  content: "•";
  position: absolute;
  color: #757575;
  font-size: 21px;
  left: 6.5px !important; 
  top: -4px !important; 
}


.redeem-btn {
  background: var(--v-primary-base) !important;
  color: white !important;
  font-weight: 600;
}

.more-detail-link {
  color: var(--v-primary-base);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 4px;
}

.point-redemption-summary-row { 
  font-size: 16px;
  font-weight: 500;
  display:flex;
  justify-content: space-between;
  margin-top: 10px;
}

.point-redemption-label,
.point-redemption-value {
  color: var(--v-primary-base);
  font-weight: 600;
}

.point-redemption-label-normal,
.point-redemption-value-normal {
  color: inherit;
  font-weight: normal;
  font-size: 14px;
}

.quantity-selector-row {
  margin-top: 16px;
  align-items: center;
}

.description-collapsed {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  width: 100%;
  list-style: none; 
  position: relative;
  padding-left: 1.425em;
}

.description-collapsed::before {
  content: "•";
  position: absolute;
  color: #757575;
  font-size: 21px;
  left: 6.5px !important;
  top: -4px !important;
}

.description-expanded {
  white-space: normal;
  display: list-item; 
}

.description-collapsed-ellipsis {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  width: 100%;
  list-style: none;  
  position: relative;
  padding-left: 1.425em;  
}
.description-collapsed-ellipsis::before {
  content: "•";
  position: absolute;
  color: #757575;
  font-size: 21px;
  left: 6.5px !important;
  top: -4px !important;
}

.custom-quantity-selector >>> .v-btn {
  min-width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white 0 !important;
  border: 2px solid var(--v-primary-base) !important;
  color: var(--v-primary-base) !important;
  padding: 0;
  box-shadow: none !important;
}

.custom-quantity-selector >>> .v-btn.v-btn--disabled {
  background-color: #f5f5f5 !important; 
  color: #bdbdbd !important;         
  opacity: 1;
  border: 2px solid #e0e0e0 !important;
}

.custom-quantity-selector >>> .quantity-display {
  font-size: 18px;
  font-weight: 600;
  color: #212121;
  margin: 0 10px;
  min-width: 30px;
  text-align: center;
}

.redeemed-item-text {
  font-size: 16px;
  color: #212121;
}

.v-divider {
  border-color: #EEEEEE !important;
  margin-top: 16px !important;
  margin-bottom: 16px !important;
}

.redemption-summary-row { 
  font-weight: 500;
  color: var(--v-primary-base) !important;
}

.offer-expiry-row {
  margin-top: 16px;
}

.text-right {
    text-align: right;
}

.offer-expiry {
  font-size: 14px;
  color: #757575;
}

.redeemed-items-section {
  padding-bottom: 10px;
  padding-right: 22px;
  padding-left: 10px;
}

.nameofitems-heading {
  font-size: 1rem; 
  font-weight: bold;
  margin-bottom: 5px;
}

.redeemed-item-entry {
  margin-left: 10px; 
  margin-bottom: 5px;
}

.item-qty {
  font-weight: bold;
  margin-right: 5px;
}

.item-name {
  font-size: 0.95rem;
}

.item-price {
  font-weight: bold;
}

.item-attribute-row {
  font-size: 0.85rem;
  color: #666;
}

.item-attribute-qty {
  font-weight: normal;
  margin-right: 3px;
}

.item-attribute-name {
  font-weight: normal; 
}

.item-attribute-price-value { 
  font-weight: normal;
  text-align: end;
}
</style>